import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBackIos as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import generatePDF, { getDaysFromDateRange } from './utils';
import renderCommentField from './components/Comments';
import Page from '../../../components/Layout/Page';
import CustomHeader from '../../../components/General/CustomHeader';
import { formatShortDate } from '../../../components/BiweeklyReport/constants';
import { BiweeklyReportProvider } from '../../../context/BiweeklyReportContext';
import AllChartsGrid from './components/AllChartsGrid';
import ChartSelectionModal from './components/ChartSelectionModal';
import IrrigationProgram from '../Riego/irrigationProgram';
import apiClient from '../../../requests/api/apiClient';
import { SectorDetailSchema } from '../../../requests/api/apiTypes';
import { ReduxState } from '../../../types';
import { BiweeklyReport } from './constants/chartConstants';
import { UserRole, UserType } from '../../../constants/user_types';

const BiweeklyReportDetail: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { selectedFarm } = useSelector((state: ReduxState) => state.accountFarm);
  const currentUser = useSelector((state: ReduxState) => state.account.user);

  // Permissions logic
  const isAdmin = currentUser?.role === UserRole.ADMIN && currentUser?.type === UserType.CDTEC;
  const isOperator = currentUser?.role === UserRole.OPERATOR
  && currentUser?.type === UserType.CDTEC;
  const isAgro = currentUser?.role === UserRole.AGRO && currentUser?.type === UserType.CDTEC;

  const canEdit = isAdmin || isOperator || isAgro;
  const [report, setReport] = useState<BiweeklyReport | null>(null);
  const [sectorInfo, setSectorInfo] = useState<SectorDetailSchema | null>(null);
  const [allSectorReports, setAllSectorReports] = useState<BiweeklyReport[]>([]);
  const [currentReportIndex, setCurrentReportIndex] = useState<number>(-1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [savingCharts, setSavingCharts] = useState(false);
  const [navigationLoading, setNavigationLoading] = useState(false);
  const [summaryData, setSummaryData] = useState<{
    irrigationSum: number;
    rainfallSum: number;
    et0Sum: number;
    kcSum: number;
  } | null>(null);
  const [comments, setComments] = useState({
    technical: '',
    technicalAdministration: '',
    operation: '',
  });
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const reportContainerRef = useRef<HTMLDivElement>(null);

  const fetchSummaryData = async (id: string) => {
    try {
      const response = await apiClient.biweeklyReport.biweeklyReportSummary(id);
      setSummaryData(response.data.summaryData);
    } catch (err) {
      console.error('Error loading summary data:', err);
    }
  };

  const handleDownloadPDF = async () => {
    if (!reportContainerRef.current || !report || !sectorInfo) {
      enqueueSnackbar('Error: No se puede generar el PDF', { variant: 'error' });
      return;
    }

    const fileName = `Informe_Quincenal_${sectorInfo.name}_${formatShortDate(report.startDate)}_${formatShortDate(report.endDate)}.pdf`;

    setIsGeneratingPDF(true);

    await generatePDF({
      element: reportContainerRef.current,
      fileName,
      onProgress: (message) => enqueueSnackbar(message, { variant: 'info' }),
      onSuccess: () => {
        enqueueSnackbar('PDF descargado exitosamente', { variant: 'success' });
        setIsGeneratingPDF(false);
      },
      onError: (errorMessage) => {
        enqueueSnackbar(errorMessage, { variant: 'error' });
        setIsGeneratingPDF(false);
      },
    });
  };

  useEffect(() => {
    const fetchReportDetail = async () => {
      try {
        setLoading(true);
        setError(null);

        const reportResponse = await apiClient.biweeklyReport.biweeklyReportDetail(reportId);
        const reportData = reportResponse.data.biweeklyReport;
        setReport(reportData);

        setComments({
          technical: reportData.technicalComment || '',
          technicalAdministration: reportData.technicalAdministrationComment || '',
          operation: reportData.operationComment || '',
        });

        await fetchSummaryData(reportId);

        if (reportData.sectorId) {
          const sectorResponse = await apiClient.sector.sectorDetail(reportData.sectorId);
          if (sectorResponse.data.sector) {
            setSectorInfo(sectorResponse.data.sector);
          }

          const allReportsResponse = await apiClient.biweeklyReport.biweeklyReportList(
            reportData.sectorId,
          );
          const sortedReports = allReportsResponse.data.biweeklyReports.sort((
            a: BiweeklyReport,
            b: BiweeklyReport,
          ) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
          setAllSectorReports(sortedReports);

          const currentIndex = sortedReports.findIndex((r: BiweeklyReport) => r.id === reportId);
          setCurrentReportIndex(currentIndex);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading biweekly report:', err);
        setError('Error al cargar el informe quincenal');
        setLoading(false);
      }
    };

    if (reportId) {
      fetchReportDetail();
    }
  }, [reportId]);

  const handleGoBack = () => {
    history.goBack();
  };

  const handleSaveReport = async () => {
    if (!report) return;

    try {
      setSavingCharts(true);

      const updateData: any = {
        technicalComment: comments.technical,
        technicalAdministrationComment: comments.technicalAdministration,
        operationComment: comments.operation,
      };
      if (report.selectedCharts && report.selectedCharts.length > 0) {
        updateData.selectedCharts = report.selectedCharts;
      }

      await apiClient.biweeklyReport.biweeklyReportUpdate(reportId, updateData);

      setReport((prev) => (prev ? {
        ...prev,
        technicalComment: comments.technical,
        technicalAdministrationComment: comments.technicalAdministration,
        operationComment: comments.operation,
      } : null));

      enqueueSnackbar('Reporte guardado exitosamente', { variant: 'success' });
    } catch (err) {
      console.error('Error saving report:', err);
      enqueueSnackbar('Error al guardar el reporte', { variant: 'error' });
    } finally {
      setSavingCharts(false);
    }
  };

  const handleEditCharts = () => {
    setChartModalOpen(true);
  };

  const handleSaveSelectedCharts = async (selectedCharts: string[]) => {
    setReport((prev) => (prev ? { ...prev, selectedCharts } : null));
    setChartModalOpen(false);
    enqueueSnackbar('Gráficos actualizados temporalmente. Presiona "CONFIRMAR Y GUARDAR" para guardar permanentemente.', {
      variant: 'info',
      autoHideDuration: 4000,
    });
  };

  const handleNavigateReport = async (direction: 'prev' | 'next') => {
    if (currentReportIndex === -1 || allSectorReports.length === 0) return;

    const newIndex = direction === 'prev' ? currentReportIndex - 1 : currentReportIndex + 1;

    if (newIndex < 0 || newIndex >= allSectorReports.length) return;

    const newReport = allSectorReports[newIndex];
    setNavigationLoading(true);

    try {
      history.replace(`/informe-quincenal/${newReport.id}`);
    } catch (err) {
      console.error('Error navigating to report:', err);
      enqueueSnackbar('Error al navegar al informe', { variant: 'error' });
    } finally {
      setNavigationLoading(false);
    }
  };

  const canNavigatePrev = currentReportIndex > 0;
  const canNavigateNext = currentReportIndex < allSectorReports.length - 1;
  const formatDateWithDashes = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).replace(/\//g, '-');
  };

  const irrigationDays = useMemo(() => {
    if (!report) return [];
    return getDaysFromDateRange(report.startDate, report.endDate);
  }, [report?.startDate, report?.endDate]);

  const breadcrumb = (() => {
    if (report && sectorInfo?.name && selectedFarm?.name) {
      return `${selectedFarm.name}/${sectorInfo.name}/Informe Quincenal/${formatDateWithDashes(report.startDate)} - ${formatDateWithDashes(report.endDate)}`;
    }
    if (report && sectorInfo?.name) {
      return `${sectorInfo.name}/Informe Quincenal/${formatDateWithDashes(report.startDate)} - ${formatDateWithDashes(report.endDate)}`;
    }
    return 'Reporte Quincenal';
  })();

  if (loading) {
    return (
      <Page>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Page>
    );
  }

  if (error || !report || !sectorInfo) {
    return (
      <Page>
        <Box p={3}>
          <Alert severity="error">
            {error || 'No se pudo cargar el informe quincenal'}
          </Alert>
          <Button variant="contained" onClick={handleGoBack} sx={{ mt: 2 }}>
            Volver
          </Button>
        </Box>
      </Page>
    );
  }

  return (
    <BiweeklyReportProvider isBiweeklyReportView={true}>
      <Page>
        <Box p={3}>
          <CustomHeader
            title={breadcrumb}
            showBackButton={true}
            onBack={handleGoBack}
            ActionButton={
              <Box display="flex" alignItems="center" gap={1}>
                <Box display="flex" alignItems="center" gap={1} mr={2}>
                  <IconButton
                    onClick={() => handleNavigateReport('prev')}
                    disabled={!canNavigatePrev || navigationLoading}
                    size="small"
                    title="Informe anterior"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ minWidth: '140px', textAlign: 'center' }}
                  >
                    {formatShortDate(report.startDate)} - {formatShortDate(report.endDate)}
                  </Typography>
                  <IconButton
                    onClick={() => handleNavigateReport('next')}
                    disabled={!canNavigateNext || navigationLoading}
                    size="small"
                    title="Informe siguiente"
                  >
                    <ArrowForwardIcon />
                  </IconButton>
                </Box>

                {canEdit && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveReport}
                    disabled={savingCharts}
                  >
                    {savingCharts ? 'Guardando...' : 'CONFIRMAR Y GUARDAR'}
                  </Button>
                )}

                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleDownloadPDF}
                  startIcon={<DownloadIcon />}
                  sx={{ ml: 1 }}
                >
                  DESCARGAR PDF
                </Button>
              </Box>
            }
          />

          <Box mt={3} ref={reportContainerRef} data-ref="report-container">
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Box display="flex" flexDirection="column" gap={3}>
                  <Paper elevation={2} sx={{ p: 2, height: 'fit-content' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6">
                        Gráficos de visualización
                      </Typography>
                      {!isGeneratingPDF && canEdit && (
                        <IconButton
                          onClick={handleEditCharts}
                          color="primary"
                          size="small"
                          title="Editar gráficos"
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                    </Box>
                    <AllChartsGrid
                      sectorId={sectorInfo.id || report.sectorId}
                      farmId={sectorInfo.farm || ''}
                      selectedCharts={report.selectedCharts}
                      startDate={report.startDate}
                      endDate={report.endDate}
                      isGeneratingPDF={isGeneratingPDF}
                    />
                  </Paper>

                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Análisis de riego
                    </Typography>
                    <IrrigationProgram
                      days={irrigationDays}
                      readOnly={true}
                    />
                  </Paper>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      General
                    </Typography>
                    <Box mb={2}>
                      <Typography variant="body2" color="textSecondary">
                        Sector
                      </Typography>
                      <Typography variant="body1">
                        {sectorInfo.name}
                      </Typography>
                    </Box>
                    <Box mb={2}>
                      <Typography variant="body2" color="textSecondary">
                        Superficie teórica [HA]
                      </Typography>
                      <Typography variant="body1">
                        {sectorInfo.theoreticalSurface || 'N/A'} ha
                      </Typography>
                    </Box>
                    <Box mb={2}>
                      <Typography variant="body2" color="textSecondary">
                        Variedad
                      </Typography>
                      <Typography variant="body1">
                        {sectorInfo.variety?.name || 'N/A'}
                      </Typography>
                    </Box>
                    <Box mb={2}>
                      <Typography variant="body2" color="textSecondary">
                        Año de plantación
                      </Typography>
                      <Typography variant="body1">
                        {sectorInfo.plantingYear || 'N/A'}
                      </Typography>
                    </Box>
                    <Box mb={2}>
                      <Typography variant="body2" color="textSecondary">
                        Tipo de suelo
                      </Typography>
                      <Typography variant="body1">
                        {sectorInfo.typeOfSoil || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Tipo de riego
                      </Typography>
                      <Typography variant="body1">
                        {sectorInfo.typeOfIrrigation || 'N/A'}
                      </Typography>
                    </Box>
                  </Paper>

                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Resumen
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Concepto</TableCell>
                            <TableCell align="right">Valor</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>Período</TableCell>
                            <TableCell align="right">
                              {formatShortDate(report.startDate)}
                              {' '}
                              -
                              {' '}
                              {formatShortDate(report.endDate)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Creado por</TableCell>
                            <TableCell align="right">{report.createdBy}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Fecha de creación</TableCell>
                            <TableCell align="right">{formatShortDate(report.createdAt)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Riego (mm)</TableCell>
                            <TableCell align="right">
                              {summaryData ? `${summaryData.irrigationSum} mm` : 'Cargando...'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Precipitación (mm)</TableCell>
                            <TableCell align="right">
                              {summaryData ? `${summaryData.rainfallSum} mm` : 'Cargando...'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>ET0 (mm)</TableCell>
                            <TableCell align="right">
                              {summaryData ? `${summaryData.et0Sum} mm` : 'Cargando...'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>KC</TableCell>
                            <TableCell align="right">
                              {summaryData ? summaryData.kcSum.toFixed(2) : 'Cargando...'}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>

                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Comentarios
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={2}>
                      {renderCommentField('Técnica', comments.technical, 'technical', isGeneratingPDF, setComments, canEdit)}
                      {renderCommentField('Administración de técnica', comments.technicalAdministration, 'technicalAdministration', isGeneratingPDF, setComments, canEdit)}
                      {renderCommentField('Operación', comments.operation, 'operation', isGeneratingPDF, setComments, canEdit)}
                    </Box>
                  </Paper>
                </Box>
              </Grid>
            </Grid>

            {canEdit && (
              <ChartSelectionModal
                open={chartModalOpen}
                onClose={() => setChartModalOpen(false)}
                selectedCharts={report.selectedCharts || []}
                onSave={handleSaveSelectedCharts}
              />
            )}
          </Box>
        </Box>
      </Page>
    </BiweeklyReportProvider>
  );
};

export default BiweeklyReportDetail;
